import { BaseService } from "../BaseService";
import { AuthService } from "../auth/auth.service";
import { environment } from '../../../../src/environments/environment';
import { HttpClient } from "@angular/common/http";
import { Router } from '@angular/router';
import { Observable, of, throwError } from "rxjs";
import { catchError, concatMap, delay, map, retryWhen, take } from 'rxjs/operators';
import { ApiResult } from "../../models/ApiResult";
import { EventStatusModel } from "../../models/api/EventStatusModel";
import { EventStatusFindResultModel } from "../../models/api/EventStatusFindResultModel";

export abstract class EventStatusBaseService extends BaseService {

    private urlPart: string = 'EventStatus';

    constructor(private http: HttpClient, private router: Router, private auth: AuthService) {
        super(http, router, auth);
        this.apiSegmentUrl = this.urlPart;
    };

    /// <summary>
    /// FIND: Retrieves a list of EventStatus records from the database
    /// /EventStatus
    /// </summary>
    public findEventStatus(page: number = 0, size: number = 0, orderBy: string = "", orderDirection: string = "ASC", filter: string = ""): Observable<EventStatusFindResultModel>
    {
        // Find EventStatus Records

        const url = `${super.getBaseUrl()}?page=${page}&size=${size}&orderBy=${orderBy}&orderDirection=${orderDirection}&filter=${filter}`;

        if (!environment.production)
            console.log("Find EventStatus: Calling " + url);

        return this.http.get<EventStatusFindResultModel>(url)
            .pipe(
                map(next => {
                    let o: EventStatusFindResultModel = Object.assign(new EventStatusFindResultModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// GET: Retrieves a single EventStatus record from the database
    /// /EventStatus/{eventstatusid}
    /// </summary>
    /// <param name="eventstatusid"></param>
    /// <returns></returns>
    public getEventStatus(eventstatusid: number): Observable<EventStatusModel>
    {

        // Get Single EventStatus Record

        const url = `${super.getBaseUrl()}/${eventstatusid}`;

        if (!environment.production)
            console.log("Get EventStatus: Calling " + url);

        return this.http.get<EventStatusModel>(url)
            .pipe(
                map(next => {
                    let o: EventStatusModel = Object.assign(new EventStatusModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// POST: Adds a new EventStatus record to the database
    /// /EventStatus/{eventstatusid}
    /// </summary>
    /// <param name="eventstatusid"></param>
    /// <returns></returns>
    public createEventStatus(newRecord: EventStatusModel): Observable<EventStatusModel>
    {
        // Add Single EventStatus Record

        const url = `${super.getBaseUrl()}`;

        if (!environment.production)
            console.log("Create EventStatus:Calling " + url);

        return this.http.post<EventStatusModel>(url, newRecord)
            .pipe(
                map(next => {
                    let o: EventStatusModel = Object.assign(new EventStatusModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// PUT: Updates an existing EventStatus record in the database
    /// /EventStatus/{eventstatusid}
    /// </summary>
    /// <param name="eventstatusid"></param>
    /// <returns></returns>
    public updateEventStatus(eventstatusid: number, updatedRecord: EventStatusModel): Observable<EventStatusModel>
    {
        // Update Single EventStatus Record

        const url = `${super.getBaseUrl()}/${eventstatusid}`;

        if (!environment.production)
            console.log("Update EventStatus: Calling " + url);

        return this.http.put<EventStatusModel>(url, updatedRecord)
            .pipe(
                map(next => {
                    let o: EventStatusModel = Object.assign(new EventStatusModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public updateEventStatusRecord(updatedRecord: EventStatusModel): Observable<EventStatusModel>
    {
        return this.updateEventStatus(updatedRecord.eventStatusID, updatedRecord);
    }

    /// <summary>
    /// DELETE: Deletes an existing EventStatus record from the database
    /// /EventStatus/{eventstatusid}
    /// </summary>
    /// <param name="eventstatusid"></param>
    /// <returns></returns>
    public deleteEventStatus(eventstatusid: number): Observable<ApiResult>
    {
        // Delete Single EventStatus Record

        const url = `${super.getBaseUrl()}/${eventstatusid}`;

        if (!environment.production)
            console.log("Delete EventStatus: Calling " + url);

        return this.http.delete<ApiResult>(url)
            .pipe(
                map(next => {
                    let o: ApiResult = new ApiResult();
                    o.success = true;
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public deleteEventStatusRecord(deleteRecord: EventStatusModel): Observable<ApiResult>
    {
        // Delete Single EventStatus Record
        return this.deleteEventStatus(deleteRecord.eventStatusID);
    }

}
